<script setup lang="ts" generic="t">
import { PsIcon as Icon, PsButton as Button } from 'ps-ui';
import { computed } from 'vue';
const props = withDefaults(
  defineProps<{
    item: t;
    parent: t | null;
    displayKey?: keyof t;
    collapseable?: boolean;
    draggable?: boolean;
    selectable?: boolean;
    depth?: number;
  }>(),
  {
    collapseable: true,
    draggable: false,
    selectable: false,
    depth: 0,
  },
);

const expanded = defineModel<boolean>('expanded', { default: false });
const selected = defineModel<boolean | null>('selected', { default: false });

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};

const defaultDisplay = computed(() => {
  return props.displayKey !== undefined
    ? props.item[props.displayKey]
    : props.item;
});

const emit = defineEmits<{
  itemDropped: [t];
}>();

const handleDroppedItem = () => emit('itemDropped', props.item);
</script>
<template>
  <div
    :class="{ 'planroom-tree-item': true, collapseable }"
    :style="{ paddingLeft: `${0.5 + depth * 1.5}rem` }"
    @drop.stop="handleDroppedItem"
  >
    <Icon
      v-if="draggable"
      icon="grip-dots-vertical"
      pack="fas"
      class="tree-item-drag-handle"
      :draggable="draggable"
    />
    <Button
      v-if="collapseable"
      class="is-text tree-collapse-toggle"
      @click="toggleExpanded"
    >
      <Icon v-if="expanded" icon="square-minus" size="lg" />
      <Icon v-else icon="square-plus" size="lg" />
    </Button>
    <span v-else class="tree-collapse-spacer" aria-hidden="true"></span>
    <o-checkbox
      v-if="selectable"
      v-model="selected"
      :aria-label="`Select ${defaultDisplay}`"
      class="item-checkbox"
    />
    <slot name="item" v-bind="{ item, expanded, selected }">
      <span>{{ defaultDisplay }}</span>
    </slot>
  </div>
</template>
<style>
.planroom-tree-item {
  --planroom-tree-control-gap: 0.5rem;
  --planroom-tree-control-size: 1.25rem;

  align-items: center;
  box-sizing: border-box;
  column-gap: var(--planroom-tree-control-gap);
  display: grid;
  grid-template-columns:
    var(--planroom-tree-control-size)
    var(--planroom-tree-control-size)
    minmax(0, 1fr);
  height: 2.125rem;
  padding: 0.125rem 0.5rem;
  width: 100%;

  .tree-collapse-toggle,
  .tree-collapse-spacer {
    align-items: center;
    box-sizing: border-box;
    display: inline-flex;
    flex: 0 0 var(--planroom-tree-control-size);
    height: var(--planroom-tree-control-size);
    justify-self: center;
    justify-content: center;
    width: var(--planroom-tree-control-size);
  }

  .tree-collapse-toggle {
    padding: 0;
  }

  .item-checkbox {
    align-self: center;
    box-sizing: border-box;
    height: var(--planroom-tree-control-size);
    justify-self: center;
    margin: 0;
    width: var(--planroom-tree-control-size);
  }

  .ps-folder,
  .ps-file {
    align-items: center;
    min-height: 1.5rem;
    min-width: 0;
  }

  .ps-folder .folder-icon,
  .ps-file .file-type {
    align-items: center !important;
    box-sizing: border-box;
    display: inline-flex !important;
    flex: 0 0 1.25rem !important;
    height: 1.25rem;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 1.25rem !important;
  }

  .ps-folder .folder-icon > span,
  .ps-file .file-type > span {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  &:hover {
    .tree-item-drag-handle {
      opacity: 25%;
    }
  }

  .tree-item-drag-handle {
    opacity: 0;

    &:hover {
      opacity: 75%;
      cursor: grab;
    }
  }

  &.dragging {
    opacity: 50%;
  }
}

@media screen and (max-width: 768px) {
  .planroom-tree-item {
    height: auto;
    min-height: 2.125rem;
  }

  .planroom-tree-item .item-checkbox {
    align-self: start;
    margin-top: 0.125rem;
  }
}
</style>
